// Persistent options store — any option toggled here survives navigation,
// reloads, and stays sticky until the user explicitly resets it. Options are
// namespaced by key, saved to localStorage, and broadcast to subscribers.
//
// Hardening (crash fix): panels park whole datasets here (globe worlds, TLE
// caches, radar bins, news). Writing the entire store synchronously on every
// tick meant stringifying hundreds of KB many times a second, which starved
// the main thread and grew the heap until the tab died. Writes are now
// coalesced, oversized values stay in memory only, and the persisted blob is
// capped.
const KEY = "sophia:persist-options";

type Store = Record<string, unknown>;

/** A single option bigger than this is kept in memory but never persisted. */
const MAX_VALUE_CHARS = 32_000;
/** Hard ceiling for the whole persisted blob. */
const MAX_TOTAL_CHARS = 200_000;
/** Coalesce window for disk writes. */
const SAVE_DEBOUNCE_MS = 400;

function load(): Store {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch { return {}; }
}

/** Keys whose current value is too big to persist — skipped on every save. */
const oversized = new Set<string>();

function serialize(s: Store): string | null {
  try {
    const out: Store = {};
    let total = 0;
    for (const [k, v] of Object.entries(s)) {
      if (oversized.has(k)) continue;
      let json: string;
      try { json = JSON.stringify(v) ?? "null"; } catch { continue; }
      if (json.length > MAX_VALUE_CHARS) { oversized.add(k); continue; }
      if (total + json.length > MAX_TOTAL_CHARS) continue;
      total += json.length;
      out[k] = v;
    }
    return JSON.stringify(out);
  } catch { return null; }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave() {
  if (typeof localStorage === "undefined") return;
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    const json = serialize(store);
    if (json === null) return;
    try { localStorage.setItem(KEY, json); } catch { /* quota — keep memory copy */ }
  }, SAVE_DEBOUNCE_MS);
}

let store: Store = load();
const listeners = new Set<(s: Store) => void>();

function notify() {
  // One snapshot for every listener instead of a copy each.
  const snapshot = store;
  for (const l of listeners) {
    try { l(snapshot); } catch { /* a listener must never break a write */ }
  }
}

export function getOption<T>(key: string, fallback: T): T {
  return (key in store ? (store[key] as T) : fallback);
}
export function setOption<T>(key: string, value: T) {
  if (store[key] === value) return;
  store = { ...store, [key]: value };
  oversized.delete(key);
  scheduleSave();
  notify();
}
export function clearOption(key: string) {
  if (!(key in store)) return;
  const next = { ...store };
  delete next[key];
  store = next;
  oversized.delete(key);
  scheduleSave();
  notify();
}
export function subscribeOptions(fn: (s: Store) => void) {
  listeners.add(fn);
  try { fn(store); } catch { /* ignore */ }
  return () => { listeners.delete(fn); };
}

/** Every option key currently held, sorted. For the Knowledge Vault browser. */
export function optionKeys(): string[] {
  return Object.keys(store).sort();
}

/** Read-only snapshot of the whole option store. */
export function snapshotOptions(): Store {
  return { ...store };
}

/** True when this key's value is too large to reach disk (memory only). */
export function isOptionOversized(key: string): boolean {
  return oversized.has(key);
}


import { useEffect, useState } from "react";
/** React hook — sticky option value. Persists to localStorage automatically. */
export function usePersistentOption<T>(key: string, fallback: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => getOption<T>(key, fallback));
  useEffect(() => subscribeOptions((s) => setV((key in s ? s[key] : fallback) as T)), [key, fallback]);
  return [v, (val: T) => setOption(key, val)];
}
